/**
 * PL-initialen in de titel van een draaidag-afspraak: "… || Kaptiv Nexus instituut - [9807] - LJ".
 *
 * Airtable vult het PL-veld alleen uit het gekoppelde project of het handmatige PL-veld — nooit uit de
 * initialen in de titel. Zonder project viel de PL dus weg (Joris, 22 sep 2026). Hier: initialen uit de
 * titel halen en, als het eenduidig is, vertalen naar een medewerker.
 */
const INITIALEN = /(?:^|\s)-\s*([A-Z][A-Za-z]{1,4})\s*\??\s*$/;

export function initialenUitTitel(titel) {
  const m = String(titel || '').trim().match(INITIALEN);
  return m ? m[1] : '';
}

/** "Evie van den Berg" → ["EB", "EvdB"]; "Lars Jansen" → ["LJ"] */
export function initialenVanNaam(naam) {
  const delen = String(naam || '').trim().split(/\s+/).filter(Boolean);
  if (delen.length < 2) return [];
  const kort = delen[0][0] + delen[delen.length - 1][0];
  const lang = delen.map((d) => d[0]).join('');
  return [...new Set([kort, lang])].map((x) => x.toUpperCase());
}

/** Zelfde normalisatie als de PL-kiezer in het detailpaneel: actieve medewerkers/stagiairs, ontdubbeld. */
export function normaliseerMedewerkers(lijst) {
  const gezien = new Set();
  return (Array.isArray(lijst) ? lijst : [])
    .filter((x) => {
      const f = x.fields || {};
      const status = f.Status || x.status;
      const type = f.Type || x.type;
      return (!status || status === 'Active')
        && (!type || ['Medewerker', 'Stagiair'].includes(Array.isArray(type) ? type[0] : type));
    })
    .map((x) => ({ id: x.id, naam: x.fields?.['Volledige naam'] || x.full_name || x.naam || x.name || '' }))
    .filter((x) => {
      if (!x.id || !x.naam) return false;
      const sleutel = x.naam.trim().toLowerCase();
      if (gezien.has(sleutel)) return false;
      gezien.add(sleutel);
      return true;
    });
}

/**
 * Initialen → medewerker. Eenduidig: { naam, voornaam, zeker: true }. Meerdere kandidaten (JM: Jade Maree,
 * Joanne Maas, Jur Magielse …) of geen match: { naam: initialen, zeker: false }.
 */
export function plUitInitialen(initialen, medewerkers) {
  const ini = String(initialen || '').toUpperCase();
  if (!ini) return null;
  const kandidaten = (medewerkers || []).filter((m) => initialenVanNaam(m.naam).includes(ini));
  if (kandidaten.length === 1) {
    const naam = kandidaten[0].naam;
    return { naam, voornaam: naam.split(/\s+/)[0], zeker: true, initialen: ini, id: kandidaten[0].id };
  }
  return { naam: ini, voornaam: ini, zeker: false, initialen: ini, kandidaten: kandidaten.map((k) => k.naam) };
}

/** PL-weergave voor een rij: Airtable-PL als die er is, anders de PL uit de titel. */
export function plVoorRij(rij, medewerkers) {
  if (rij?.pl) return { tekst: rij.pl, uitTitel: false };
  const ini = initialenUitTitel(rij?.origineleTitel);
  if (!ini) return { tekst: '', uitTitel: false };
  const r = plUitInitialen(ini, medewerkers);
  return { tekst: r.voornaam, uitTitel: true, zeker: r.zeker, initialen: ini, kandidaten: r.kandidaten || [] };
}
